import { type RefObject, useLayoutEffect, useState } from "react";
import { type Point, type Position, type Rect, type Size, toRect, viewportSize } from "./geometry";
import { placeMenu, placeSubmenu } from "./placement";

/** Computes where a panel of `size` goes; undefined when its anchor is gone. */
export type PlacePanel = (size: Size, viewport: Size) => Position | undefined;

/** The root panel: at the pointer, or below the element it was opened from. */
export function rootPlacement(anchor: { point: Point; rect: Rect | null }): PlacePanel {
  return (size, viewport) => placeMenu(anchor, size, viewport);
}

/** A submenu: beside its parent item and panel, both read when the submenu appears. */
export function submenuPlacement(item: () => HTMLElement | null, parent: () => HTMLElement | null): PlacePanel {
  return (size, viewport) => {
    const itemEl = item();
    const parentEl = parent();
    if (!itemEl || !parentEl) return undefined;
    return placeSubmenu(toRect(itemEl.getBoundingClientRect()), toRect(parentEl.getBoundingClientRect()), size, viewport);
  };
}

/** Measures the panel once it is in the DOM and places it; undefined until then. */
export function usePanelPosition(panelRef: RefObject<HTMLElement | null>, place: PlacePanel): Position | undefined {
  const [position, setPosition] = useState<Position>();
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const next = place({ width: panel.offsetWidth, height: panel.offsetHeight }, viewportSize());
    if (next) setPosition(next);
    // Position once per open; content changes (a toggled checkbox) must not move the menu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return position;
}
